import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { FocusHandler, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import ContentItem from '@smart-tv/components/content-item/content-item';
import Button from '@smart-tv/components/button/button';
import DeleteIcon from '@smart-tv/components/icons/delete';
import { focusKeyPrevAtom } from '@smart-tv/atoms';
import { ContentCover, ContentLayout, ContentType } from '@smart-tv/typings/content';
import { SearchResultItem } from '@smart-tv/typings/search';
import addQueryParams from '@smart-tv/utils/routing/add-query-params';
import encodeProgramSheetParams from '@smart-tv/utils/routing/encode-program-sheet-params';
import addToSearchHistory from '@smart-tv/utils/kmm/addToSearchHistory';
import { getSearchHistoryRemoveFocusKey } from './search-focus-keys';
import styles from './search.module.scss';

interface Props {
  item: SearchResultItem;
  index: number;
  onFocus?: FocusHandler;
  onRemove?: () => void;
  onAdd?: (item: SearchResultItem) => void;
}

export const SearchResultCard: React.FC<Props> = ({ item, index, onFocus, onRemove, onAdd }) => {
  const navigate = useNavigate();
  const [, setFocusKeyPrev] = useAtom(focusKeyPrevAtom);
  const [animating, setAnimating] = React.useState(false);

  const contentType = item.contentType === 'SERIES' ? ContentType.Series : ContentType.Movies;
  const focusKey = `SEARCH_RESULT_${item.contentId}`;

  const handleActivate = React.useCallback(() => {
    setAnimating(true);
    onAdd?.(item);
    addToSearchHistory(item).catch((error) => {
      console.error(error);
    });
    setFocusKeyPrev({ focusKeyPrev: focusKey, isLiveTV: false });
    navigate(
      addQueryParams({
        programsheet: encodeProgramSheetParams(undefined, undefined, item.contentId, contentType)
      })
    );
  }, [navigate, item, contentType, onAdd, focusKey, setFocusKeyPrev]);

  const { ref, focused } = useFocusable({
    focusKey,
    onEnterPress: handleActivate,
    onFocus
  });

  const contentItem = React.useMemo(() => {
    const isMovie = contentType === ContentType.Movies;

    return {
      contentId: item.contentId,
      contentType,
      contentLayout: isMovie ? ContentLayout.Mixed : ContentLayout.Landscape,
      cover: item.coverImageUrl ?? '',
      coverType: isMovie ? ContentCover.Portrait : ContentCover.Landscape,
      duration: 0,
      key: `search-result-${item.contentId}`,
      title: item.title ?? undefined,
      favorited: item.isCustomerFavorite,
      accessLogo: item.isSubscriptionOnly ? ('subscription' as const) : undefined
    };
  }, [item, contentType]);

  return (
    <div>
      <ContentItem
        ref={ref}
        contentItem={contentItem}
        contentSliderIndex={index}
        liveTV={false}
        animating={animating}
        setAnimating={setAnimating}
        focused={focused}
        onClick={handleActivate}
        className={styles['search__result-item-content']}
      />
      {onRemove && (
        <Button
          focusKey={getSearchHistoryRemoveFocusKey(item.contentId)}
          variant="button"
          onClick={onRemove}
          className={styles['search__result-remove']}
        >
          <DeleteIcon size="lg" />
          Fjern
        </Button>
      )}
    </div>
  );
};

export default React.memo(SearchResultCard);
